import React, { Fragment, useMemo, useState } from "react";
import { MultiSectionLayout } from "@jugl-web/ui-components";
import {
  SideMenuDivider,
  SideMenuList,
  SideMenuListItem,
  SideMenuPanel,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { LiveChatAppAccountMenuTab } from "./types";
import { ReactComponent as ChatStartersActiveIcon } from "./assets/chat-starters-active.svg";
import { ReactComponent as ChatStartersIcon } from "./assets/chat-starters.svg";
import { ReactComponent as GeneralInfoActiveIcon } from "./assets/general-info-active.svg";
import { ReactComponent as GeneralInfoIcon } from "./assets/general-info.svg";
import { ReactComponent as InstallActiveIcon } from "./assets/install-active.svg";
import { ReactComponent as InstallIcon } from "./assets/install.svg";
import { ReactComponent as QuickActionsActiveIcon } from "./assets/quick-actions-active.svg";
import { ReactComponent as QuickActionsIcon } from "./assets/quick-actions.svg";
import { ReactComponent as CustomButtonActiveIcon } from "./assets/custom-button-active.svg";
import { ReactComponent as CustomButtonIcon } from "./assets/custom-button.svg";
import { InstallTabContent } from "./components/InstallTabContent";
import { CustomButtonTabContent } from "./components/CustomButtonTabContent";
import { GeneralInfoTabContent } from "./components/GeneralInfoTabContent";
import { QuickActionsTabContent } from "./components/QuickActionsTabContent";
import { ChatStarterTabContent } from "./components/ChatStarterTabContent";

export const LiveChatAppAccountScreen: React.FC = () => {
  const [selectedMenuTab, setSelectedMenuTab] =
    useState<LiveChatAppAccountMenuTab>(LiveChatAppAccountMenuTab.install);

  const { requestNavigation } = useNavigationGuardContext();

  const { t } = useTranslations();

  const sideMenuSections = useMemo(
    () => [
      [
        {
          id: LiveChatAppAccountMenuTab.install,
          label: t({
            id: "client-support-page.install",
            defaultMessage: "Install",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.install ? (
              <InstallActiveIcon />
            ) : (
              <InstallIcon />
            ),
        },
      ],
      [
        {
          id: LiveChatAppAccountMenuTab.generalInfo,
          label: t({
            id: "client-support-page.general-info",
            defaultMessage: "General Info",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.generalInfo ? (
              <GeneralInfoActiveIcon />
            ) : (
              <GeneralInfoIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.chatStarter,
          label: t({
            id: "client-support-page.chat-starters",
            defaultMessage: "Chat Starters",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.chatStarter ? (
              <ChatStartersActiveIcon />
            ) : (
              <ChatStartersIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.customButton,
          label: t({
            id: "client-support-page.custom-button",
            defaultMessage: "Custom Button",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.customButton ? (
              <CustomButtonActiveIcon />
            ) : (
              <CustomButtonIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.quickActions,
          label: t({
            id: "client-support-page.quick-actions",
            defaultMessage: "Quick Actions",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.quickActions ? (
              <QuickActionsActiveIcon />
            ) : (
              <QuickActionsIcon />
            ),
        },
      ],
    ],
    [selectedMenuTab, t]
  );

  return (
    <MultiSectionLayout
      leftSide={
        <SideMenuPanel>
          {sideMenuSections.map((section, index) => {
            const isLastSection = index === sideMenuSections.length - 1;

            return (
              <Fragment key={+index}>
                <SideMenuList>
                  {section.map((item) => (
                    <SideMenuListItem
                      key={item.id}
                      label={item.label}
                      startIcon={item.startIcon}
                      isSelected={selectedMenuTab === item.id}
                      onClick={() =>
                        requestNavigation(() => setSelectedMenuTab(item.id))
                      }
                    />
                  ))}
                </SideMenuList>
                {!isLastSection && <SideMenuDivider />}
              </Fragment>
            );
          })}
        </SideMenuPanel>
      }
    >
      {selectedMenuTab === LiveChatAppAccountMenuTab.install && (
        <InstallTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.generalInfo && (
        <GeneralInfoTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.chatStarter && (
        <ChatStarterTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.customButton && (
        <CustomButtonTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.quickActions && (
        <QuickActionsTabContent />
      )}
    </MultiSectionLayout>
  );
};
